import React, { useState, ReactElement, useEffect } from 'react'
import { makeStyles, createStyles, useTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import Grid from '@bit/atd.web.grid'
import Button from '@bit/atd.web.button'
import InputText from '@bit/atd.web.forms.input-text'
import Icons from '@bit/atd.web.icons'
import colors from '@bit/atd.web.colors'
import useDebounce from 'modules/common/hooks/useDebounce'
import CustomStats from '../Widgets/Stats'
import resultSets from '../../config/resultSets'
import { TrackingEvents } from '../../../../constants/trackingEvents'

const useStyles = makeStyles((theme) =>
  createStyles({
    inputWrapper: {
      background: colors.white,
      padding: '20px 0px',
      justifyContent: 'center',
    },
    inputPanel: {
      width: '50%',
      [theme.breakpoints.down('xs')]: {
        width: '100%',
        background: colors.white,
        borderBottom: 'none',
      },
    },
    searchInputWrapper: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flex: 1,
      borderBottom: `1px solid ${colors.gray400}`,
      [theme.breakpoints.down('xs')]: {
        margin: '0 16px 16px',
        background: colors.gray200,
      },
      '& > button': {
        padding: 0,
        minWidth: 'auto',
        marginRight: '8px',
        [theme.breakpoints.down('xs')]: {
          marginRight: '3px',
        },
      },
    },
    searchInput: {
      flex: 1,
      '& input': {
        fontSize: '24px',
        lineHeight: '32px',
        [theme.breakpoints.down('xs')]: {
          padding: '11px 3px',
          fontWeight: 500,
          fontSize: '16px',
          lineHeight: '16px',
        },
      },
      '& fieldset': {
        border: 'none',
      },
    },
    closeSearchBtn: {
      '&:hover, &:active': {
        background: 'transparent',
      },
      [theme.breakpoints.down('xs')]: {
        marginLeft: '3px',
        marginRight: '0 !important',
      },
    },
    resultInfo: {
      height: '20px',
      margin: '5px 16px',
      fontWeight: 600,
      lineHeight: '20px',
    },
    hide: {
      opacity: 0,
    },
  }),
)

type PropTypes = {
  currentRefinement: string
  refine: (arg: string) => void
  isSearchStalled: boolean
  index: string
}

/**
 *
 * @param props
 * @constructor
 */
const SearchInput = (props: PropTypes): ReactElement => {
  const { currentRefinement, refine, index } = props
  const classes = useStyles({ showOverlay: false })
  const theme = useTheme()
  const mobile = useMediaQuery(theme.breakpoints.down('xs'))
  const [searchText, setSearchText] = useState(currentRefinement)
  const searchKey = useDebounce(searchText)
  const searchKeyAnalytics = useDebounce(searchText, 900)

  useEffect(() => {
    if (searchKey !== currentRefinement) {
      refine(searchKey)
    }
  }, [searchKey])

  useEffect(() => {
    // on search search key update,send event to segment
    if (searchKeyAnalytics !== '') {
      analytics.track(TrackingEvents.PRODUCTS_SEARCHED, {
        query: searchKeyAnalytics,
      })
    }
  }, [searchKeyAnalytics])

  useEffect(() => {
    if (currentRefinement !== searchText) {
      setSearchText(currentRefinement)
    }
  }, [currentRefinement])

  const onClearSearch = () => {
    refine('')
  }

  return (
    <Grid container className={classes.inputWrapper}>
      <Grid item xs={12} md={6} className={classes.inputPanel}>
        <div>
          <div className={classes.searchInputWrapper}>
            {mobile && (
              <Button
                className={classes.closeSearchBtn}
                variant="tertiary"
                onClick={onClearSearch}
              >
                <Icons glyph="caret-left" size={20} color={colors.gray800} />
              </Button>
            )}
            <InputText
              className={classes.searchInput}
              value={searchText}
              placeholder="Your search query..."
              onChange={(event) => {
                setSearchText(event.target.value)
              }}
            />
            {searchText && (
              <Button variant="tertiary" onClick={onClearSearch}>
                <Icons glyph="close" size={16} color={colors.gray800} />
              </Button>
            )}
          </div>
        </div>
        <div className={classes.resultInfo}>
          <CustomStats
            title={searchKey ? `for '${searchKey}'` : ''}
            resultName={'Result'}
            indexes={
              index === 'master'
                ? resultSets.map(({ index }) => index)
                : undefined
            }
          />
        </div>
      </Grid>
    </Grid>
  )
}

export default SearchInput
